import i18n from 'i18next';

const defaults = {
  symbol: '€ ',
  separator: '.',
  decimal: ',',
  formatWithSymbol: true,
  errorOnInvalid: false,
  precision: 2
};

const round = v => Math.round(v);
const pow = p => Math.pow(10, p);
const rounding = (value, increment) => round(value / increment) * increment;

const groupRegex = /(\d)(?=(\d{3})+\b)/g;
const vedicRegex = /(\d)(?=(\d\d)+\d\b)/g;

/**
 * Create a new instance of currency.js
 * @param {number|string|currency} value
 * @param {object} [opts]
 */
export function currency(value, opts) {
  let that = this;

  if (!(that instanceof currency)) {
    return new currency(value, opts);
  }

  let settings = Object.assign({}, defaults, opts)
    , precision = pow(settings.precision)
    , v = parse(value, settings);

  that.intValue = v;
  that.value = v / precision;

  // Set default incremental value
  settings.increment = settings.increment || (1 / precision);

  // Support vedic numbering systems
  // see: https://en.wikipedia.org/wiki/Indian_numbering_system
  if (settings.useVedic) {
    settings.groups = vedicRegex;
  } else {
    settings.groups = groupRegex;
  }

  // Intended for internal usage only - subject to change
  this._settings = settings;
  this._precision = precision;
}

function parse(value, opts, useRounding = true) {
  let v = 0
    , { decimal, errorOnInvalid, precision: decimals } = opts
    , precision = pow(decimals)
    , isNumber = typeof value === 'number';

  if (isNumber || value instanceof currency) {
    v = ((isNumber ? value : value.value) * precision);
  } else if (typeof value === 'string') {
    let regex = new RegExp('[^-\\d' + decimal + ']', 'g')
      , decimalString = new RegExp('\\' + decimal, 'g');
    v = value
      .replace(/\((.*)\)/, '-$1')   // allow negative e.g. (1.99)
      .replace(regex, '')           // replace any non numeric values
      .replace(decimalString, '.')  // convert any decimal values
      * precision;                  // scale number to integer value
    v = v || 0;
  } else {
    if (errorOnInvalid) {
      throw Error('Invalid Input');
    }
    v = 0;
  }

  // Handle additional decimal for proper rounding
  v = v.toFixed(1);

  return useRounding ? round(v) : v;
}

currency.prototype = {
  add(number) {
    let { intValue, _settings, _precision } = this;
    return currency((intValue += parse(number, _settings)) / _precision, _settings);
  },

  format(useSymbol) {
    let { formatWithSymbol, symbol, separator, decimal, groups } = this._settings;

    // set symbol formatting
    typeof (useSymbol) === 'undefined' && (useSymbol = formatWithSymbol);

    let values = ((useSymbol ? symbol : '') + this).split('.')
      , dollars = values[0]
      , cents = values[1];
    const digits = Number(((Number('' + this) * 100) / 100).toFixed(2)).toLocaleString(i18n.language, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    // return `${dollars.replace(groups, '$1' + separator)}${cents ? decimal + cents : ''}`;
    return `${useSymbol ? symbol : ''}${digits}`;
  },

  toString() {
    let { intValue, _precision, _settings } = this;
    return rounding(intValue / _precision, _settings.increment).toFixed(_settings.precision);
  },
};

export const currencyDecimal = () => {
  const val = Number(1000.01.toFixed(2)).toLocaleString(i18n.language);
  return {
    decimal: val.slice(-3, -2),
    separator: val.slice(1, 2)
  };
};

export const currencyNameToSymbol = currencyName => {
  if (typeof currencyName === 'string') {
    if (currencyName.match(/usd/i)) {
      return '$ ';
    } else if (currencyName.match(/eur/i)) {
      return '€ ';
    }
    return '';
  }
  return '';
};